import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/context/LanguageContext";
import { formatCzkPrice } from "@/lib/price";
import type { PublicWatchDogOffer } from "@/types";
import { DestructiveTrashIcon } from "@/components/ui/DestructiveTrashIcon";
import Image from "next/image";
import { useState } from "react";

type RankedEstateSlideProps = {
  offer: PublicWatchDogOffer;
  rank: number;
  city: string;
};

export function RankedEstateSlide({
  offer,
  rank,
  city,
}: RankedEstateSlideProps) {
  const fallbackImage = "/image/membership/star4.png";
  const [imgSrc, setImgSrc] = useState(
    offer.mainPicUrl?.includes("empty.gif")
      ? fallbackImage
      : offer.mainPicUrl || fallbackImage,
  );

  return (
    <div className="relative size-full overflow-hidden rounded-2xl bg-softiq-secondary shadow-lg">
      <Image
        src={imgSrc}
        alt={offer.header || "Nabídka nemovitosti"}
        fill
        loading="lazy"
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="object-cover"
        onError={() => setImgSrc(fallbackImage)}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/5 to-black/90" />
      <div className="absolute inset-x-0 top-0 flex items-start justify-between p-6 sm:p-8">
        <span className="text-5xl font-black leading-none tracking-tighter text-white drop-shadow-lg sm:text-6xl">
          #{rank}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
        <p className="max-w-full break-words text-3xl font-black leading-none tracking-tight drop-shadow-lg sm:text-4xl xl:text-5xl">
          {formatCzkPrice(offer.price)}
        </p>

        <p className="mt-3 text-xl font-semibold tracking-wide drop-shadow-md sm:text-2xl">
          {city}
        </p>
      </div>
    </div>
  );
}

type RankedEstateHideActionProps = {
  onHide: () => void | Promise<void>;
};

export function RankedEstateHideAction({
  onHide,
}: RankedEstateHideActionProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      action: "Nehodí se, skrýt inzerát",
      title: "Opravdu chcete skrýt tento inzerát?",
      description:
        "Inzerát se už nebude zobrazovat ve výsledcích. Tuto akci nelze vrátit zpět.",
      cancel: "Zrušit",
      confirm: "Skrýt inzerát",
    },
    en: {
      action: "Not a match, hide listing",
      title: "Do you really want to hide this listing?",
      description:
        "The listing will no longer appear in your results. This action cannot be undone.",
      cancel: "Cancel",
      confirm: "Hide listing",
    },
  }[language];

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="h-12 w-full rounded-xl border border-border/70 text-sm font-semibold text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
        >
          <DestructiveTrashIcon className="size-4" />
          {copy.action}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{copy.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onHide}
            className="bg-red-600 hover:bg-red-700"
          >
            {copy.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
